import React, { useEffect, useMemo, useState } from 'react';
import {
  TimelineWrapper,
  TimelineItem,
  DotContainer,
  TimelineLine,
  Dot,
  DotContent,
  TodayLine,
  ContentWrapper,
  ContentText,
  dotDiameter,
  ContentName,
} from './styled';
import { TitleWrapper } from '@src/pages/SelfServeDashboard/AccountMetricsPanel/styled';
import { Select, Tooltip } from 'antd';
import { CaretRightOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { orderedTimeFields, timeFieldDisplayNames } from '@src/pages/SelfServeDashboard/constant';
import moment from 'moment';
import { ICustomerOnboardingTimeline } from '@src/pages/SelfServeDashboard/typed';

const TimelineContent: React.FC<{
  active?: boolean;
  children?: React.ReactNode;
}> = ({ active, children }) => {
  return (
    <ContentWrapper>
      <ContentText active={active}>{children}</ContentText>
    </ContentWrapper>
  );
};

const TimelineNode: React.FC<{
  activeDot?: boolean;
  activeLine?: boolean;
}> = ({ activeDot, activeLine }) => (
  <DotContainer>
    <DotContent>
      <Dot active={activeDot} />
    </DotContent>
    <TimelineLine active={activeLine} />
  </DotContainer>
);

const TimelineEndNode: React.FC<{
  active?: boolean;
}> = ({ active }) => (
  <DotContainer>
    <DotContent>
      <Dot active={active} />
    </DotContent>
    <TimelineLine style={{ borderColor: '#d9d9d9', right: 14 }} />
    <CaretRightOutlined
      style={{
        position: 'absolute',
        right: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#000',
      }}
    />
  </DotContainer>
);

const TodayMarker: React.FC = () => (
  <>
    <DotContainer
      style={{
        width: `calc(50% - ${dotDiameter / 2}px)`,
        left: `calc(50% + ${dotDiameter / 2}px)`,
      }}
    >
      <TodayLine />
      <TimelineLine style={{ left: '2px' }} />
    </DotContainer>
    <ContentWrapper
      style={{
        position: 'absolute',
        bottom: 22,
        left: `calc(50% - ${dotDiameter / 2}px)`,
      }}
    >
      <ContentText>
        <div style={{ color: '#52C41A' }}>Today</div>
      </ContentText>
    </ContentWrapper>
  </>
);

const Timeline: React.FC<{ data?: ICustomerOnboardingTimeline[]; user?: any }> = ({
  data,
  user,
}) => {
  const defaultCustomerId = data?.[0]?.customer_id;
  const selectedCustomerId = data?.some((item) => item.customer_id === user?.customerId)
    ? user?.customerId
    : defaultCustomerId;

  const [customerId, setCustomerId] = useState(selectedCustomerId);

  useEffect(() => {
    if (selectedCustomerId) {
      setCustomerId(selectedCustomerId);
    }
  }, [selectedCustomerId]);

  const options = useMemo(
    () =>
      data?.map((c) => ({
        label: c?.customer_name,
        value: c?.customer_id,
      })) || [],
    [data],
  );

  const customerTimelineData = useMemo(
    () => data?.find((item) => item.customer_id === customerId),
    [data, customerId],
  );

  if (!data?.length) return null;

  const renderTimelineItem = (timeKey: string, index: number, array) => {
    const timeValue = customerTimelineData?.[timeKey];
    const timeDisplayValue = timeValue ? moment(timeValue).format('YYYY/MM/DD HH:mm') : '--';
    const timeDisplayName = timeFieldDisplayNames[timeKey].text;
    const timeDisplayNameTooltip = timeFieldDisplayNames[timeKey]?.tooltip;

    const nextTimeKey = orderedTimeFields?.[index + 1];
    const nextTimeValue = nextTimeKey ? customerTimelineData?.[nextTimeKey] : null;

    // If Conviva Employee assist the customer with the integration, `access_welcome_page_at` and `access_integrate_page_at` may not be captured.
    const remainingTimeKeys = orderedTimeFields.slice(index + 1);
    const hasRemainingTimeValue = remainingTimeKeys.some((key) => !!customerTimelineData?.[key]);

    const isLastNode = index === array.length - 1;

    const textContent = (
      <>
        <ContentName>
          <span>{timeDisplayName}</span>
          {timeDisplayNameTooltip ? (
            <Tooltip title={timeDisplayNameTooltip}>
              <InfoCircleOutlined />
            </Tooltip>
          ) : null}
        </ContentName>
        <div>{timeDisplayValue}</div>
      </>
    );

    if (isLastNode) {
      {
        /* End node */
      }
      return (
        <TimelineItem style={{ flex: 0.35 }} key={timeKey}>
          <TimelineEndNode active={!!timeValue} />
          <TimelineContent active={!!timeValue}>{textContent}</TimelineContent>
        </TimelineItem>
      );
    }

    if (!timeValue) {
      // Unactivated node
      return (
        <TimelineItem key={timeKey}>
          <TimelineNode />
          <TimelineContent>{textContent}</TimelineContent>
        </TimelineItem>
      );
    }

    if (!nextTimeValue) {
      if (hasRemainingTimeValue) {
        // If the next time field has no value, but later ones do,
        // it likely means the integration was assisted internally by Conviva Employee.
        return (
          <TimelineItem key={timeKey}>
            <TimelineNode activeDot />
            <TimelineContent active>{textContent}</TimelineContent>
          </TimelineItem>
        );
      } else {
        // The current period is the last valid time → today's node
        return (
          <TimelineItem key={timeKey}>
            <TimelineNode activeDot activeLine />
            <TimelineContent active>{textContent}</TimelineContent>
            <TodayMarker />
          </TimelineItem>
        );
      }
    }

    // Activate node
    return (
      <TimelineItem key={timeKey}>
        <TimelineNode activeDot activeLine />
        <TimelineContent active>{textContent}</TimelineContent>
      </TimelineItem>
    );
  };

  return (
    <div>
      <TitleWrapper>A Customer’s Onboarding Timeline</TitleWrapper>

      <Select
        options={options}
        value={customerId}
        onChange={(value) => setCustomerId(value)}
        style={{ width: 320, marginBottom: 10 }}
        showSearch
        filterOption={(input, option) =>
          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
        }
      />
      <TimelineWrapper>
        {/* Starting node */}
        <TimelineItem style={{ flex: 0.05 }}>
          <DotContainer>
            <TimelineLine style={{ borderColor: '#d9d9d9', left: 0 }} />
          </DotContainer>
        </TimelineItem>

        {/* Time node */}
        {orderedTimeFields.map(renderTimelineItem)}
      </TimelineWrapper>
    </div>
  );
};

export default Timeline;
